/**
 * PiPPanel — renders a single picture-in-picture camera overlay.
 *
 * Each panel has its own WebGL context (separate <Canvas>), positioned
 * absolutely over the main viewer using normalized viewport coordinates.
 * The camera is fixed at the position/target defined by camera_pip() in
 * OpenSCAD code and is unaffected by main-camera orbit controls.
 *
 * Rounded corners are achieved by wrapping the Canvas in a div with
 * overflow:hidden and border-radius, clipping the WebGL output natively.
 *
 * Full fidelity: uses the same SceneLighting (with custom lights), the same
 * material system (createMaterial with scene settings + per-object overrides),
 * and PostProcessing (bloom) as the main viewer. skipEnvironment=true prevents
 * per-canvas HDR env-map GPU allocation.
 *
 * Interaction:
 *  - Click anywhere on the panel body → onCameraClick (animates main camera)
 *  - ⋮ button (top-left)             → context menu: size / position / FOV presets
 *  - Drag handle (bottom-right)       → onDragStart, handed off to PiPOverlays
 *    A 4 px threshold distinguishes a handle tap (→ onCameraClick) from a drag.
 */

import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { PerspectiveCamera, OrthographicCamera } from '@react-three/drei';
import * as THREE from 'three';
import type { PiPCamera, SceneLight } from '@/worker/types';
import type { ColoredGeometry } from '@/components/viewer/types';
import type {
  SceneSettings,
  MaterialSettings,
} from '@/contexts/SceneSettingsContext';
import { SceneBackground } from '@/components/viewer/scene/SceneBackground';
import { SceneLighting } from '@/components/viewer/scene/SceneLighting';
import { PostProcessing } from '@/components/viewer/postprocessing/PostProcessing';
import { createMaterial } from '@/components/viewer/materials/materialUtils';
import { PiPContextMenu, type PiPParamChanges } from './PiPContextMenu';

// ---------------------------------------------------------------------------
// Solid mesh for a single colored geometry
// ---------------------------------------------------------------------------

function ColoredMeshItem({
  cg,
  materialSettings,
}: {
  cg: ColoredGeometry;
  materialSettings: MaterialSettings;
}) {
  const color = useMemo(
    () => new THREE.Color(cg.color[0], cg.color[1], cg.color[2]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cg.color[0], cg.color[1], cg.color[2]],
  );

  const material = useMemo(
    () =>
      createMaterial(color, materialSettings, {
        perObject: cg.material ?? undefined,
        isGhost: cg.isGhost,
        opacity: cg.isGhost ? 0.3 : cg.color[3],
        transparent: cg.color[3] < 1 || !!cg.isGhost,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [color, materialSettings, cg.material, cg.isGhost, cg.color[3]],
  );

  useEffect(() => () => material.dispose(), [material]);

  return <mesh geometry={cg.geometry} material={material} />;
}

// ---------------------------------------------------------------------------
// Model renderer
// ---------------------------------------------------------------------------

function PiPModelRenderer({
  geometry,
  coloredGeometries,
  color,
  materialSettings,
}: {
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  color: string;
  materialSettings: MaterialSettings;
}) {
  if (coloredGeometries.length > 0) {
    return (
      <group rotation={[-Math.PI / 2, 0, 0]}>
        {coloredGeometries.map((cg) => (
          <ColoredMeshItem
            key={cg.geometry.uuid}
            cg={cg}
            materialSettings={materialSettings}
          />
        ))}
      </group>
    );
  }

  if (geometry) {
    return (
      <group rotation={[-Math.PI / 2, 0, 0]}>
        <mesh geometry={geometry}>
          <meshStandardMaterial
            color={color}
            roughness={materialSettings.roughness}
            metalness={materialSettings.metalness}
            envMapIntensity={materialSettings.envMapIntensity}
            flatShading={materialSettings.flatShading}
          />
        </mesh>
      </group>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Camera setup
// ---------------------------------------------------------------------------

function PiPCameraOrientor({ camera }: { camera: PiPCamera }) {
  const { camera: threeCamera, invalidate } = useThree();

  useEffect(() => {
    threeCamera.position.set(...camera.position);

    if (camera.type === 'orthographic') {
      const ortho = threeCamera as THREE.OrthographicCamera;
      ortho.zoom = 500 / Math.max(camera.fov, 1);
    } else {
      const persp = threeCamera as THREE.PerspectiveCamera;
      persp.fov = camera.fov;
    }

    threeCamera.lookAt(camera.target[0], camera.target[1], camera.target[2]);
    threeCamera.updateProjectionMatrix();
    invalidate();
  }, [
    threeCamera,
    invalidate,
    camera.position[0],
    camera.position[1],
    camera.position[2],
    camera.target[0],
    camera.target[1],
    camera.target[2],
    camera.fov,
    camera.type,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function PiPCameraSetup({ camera }: { camera: PiPCamera }) {
  if (camera.type === 'orthographic') {
    const zoom = 500 / Math.max(camera.fov, 1);
    return (
      <>
        <OrthographicCamera
          makeDefault
          position={camera.position}
          zoom={zoom}
          near={0.01}
          far={5000}
        />
        <PiPCameraOrientor camera={camera} />
      </>
    );
  }

  return (
    <>
      <PerspectiveCamera
        makeDefault
        position={camera.position}
        fov={camera.fov}
        near={0.1}
        far={5000}
      />
      <PiPCameraOrientor camera={camera} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Drag handle icon — 2×3 dot grid
// ---------------------------------------------------------------------------

function DragGripIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="currentColor"
      aria-hidden="true"
    >
      {[0, 4, 8].map((cy) =>
        [0, 4].map((cx) => (
          <circle key={`${cx}-${cy}`} cx={cx + 2} cy={cy + 2} r={1.2} />
        )),
      )}
    </svg>
  );
}

function ResizeGripIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="currentColor"
      aria-hidden="true"
    >
      {/* Three diagonal lines — standard resize corner indicator */}
      <line
        x1="9"
        y1="3"
        x2="3"
        y2="9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="9"
        y1="6"
        x2="6"
        y2="9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Public PiPPanel component
// ---------------------------------------------------------------------------

export interface PiPPanelProps {
  camera: PiPCamera;
  geometry?: THREE.BufferGeometry;
  coloredGeometries: ColoredGeometry[];
  color: string;
  backgroundColor: string;
  sceneSettings: SceneSettings;
  lights: SceneLight[];
  /** Called when the user clicks the panel body (not after a drag). */
  onCameraClick?: (camera: PiPCamera) => void;
  /** True while this panel is being dragged — reduces opacity. */
  isDragging?: boolean;
  /** True while this panel is being resized — reduces opacity. */
  isResizing?: boolean;
  /** Called when the user starts dragging the move handle (top-right). */
  onDragStart?: (e: PointerEvent) => void;
  /** Called when the user starts dragging the resize handle (bottom-right). */
  onResizeStart?: (e: PointerEvent) => void;
  /**
   * Called when the user picks an option from the context menu.
   * The parent applies the changes to the editor source via regex replace.
   */
  onApplyChanges?: (camera: PiPCamera, changes: PiPParamChanges) => void;
}

export function PiPPanel({
  camera,
  geometry,
  coloredGeometries,
  color,
  backgroundColor,
  sceneSettings,
  lights,
  onCameraClick,
  isDragging = false,
  isResizing = false,
  onDragStart,
  onResizeStart,
  onApplyChanges,
}: PiPPanelProps) {
  const [hovered, setHovered] = useState(false);
  const [handleHovered, setHandleHovered] = useState(false);
  const [resizeHandleHovered, setResizeHandleHovered] = useState(false);

  const clickable = onCameraClick !== undefined;
  const draggable = onDragStart !== undefined;
  const resizable = onResizeStart !== undefined;
  const hasMenu = onApplyChanges !== undefined;

  // Track pointer-down on the move grip to distinguish click vs drag
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const didDrag = useRef(false);

  const handleGripPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!draggable) return;
      e.stopPropagation();
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      didDrag.current = false;

      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

      const onWindowMove = (me: PointerEvent) => {
        if (!dragStartPos.current) return;
        const dx = me.clientX - dragStartPos.current.x;
        const dy = me.clientY - dragStartPos.current.y;
        if (!didDrag.current && Math.sqrt(dx * dx + dy * dy) > 4) {
          didDrag.current = true;
          onDragStart(me);
        }
      };

      const onWindowUp = () => {
        window.removeEventListener('pointermove', onWindowMove);
        window.removeEventListener('pointerup', onWindowUp);
        dragStartPos.current = null;
        // Treat a non-drag grip tap as a panel click
        if (!didDrag.current && clickable) {
          onCameraClick!(camera);
        }
        didDrag.current = false;
      };

      window.addEventListener('pointermove', onWindowMove);
      window.addEventListener('pointerup', onWindowUp);
    },
    [camera, clickable, draggable, onCameraClick, onDragStart],
  );

  // Track pointer-down on the resize grip
  const resizeStartPos = useRef<{ x: number; y: number } | null>(null);
  const didResize = useRef(false);

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!resizable) return;
      e.stopPropagation();
      resizeStartPos.current = { x: e.clientX, y: e.clientY };
      didResize.current = false;

      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

      const onWindowMove = (me: PointerEvent) => {
        if (!resizeStartPos.current) return;
        const dx = me.clientX - resizeStartPos.current.x;
        const dy = me.clientY - resizeStartPos.current.y;
        if (!didResize.current && Math.sqrt(dx * dx + dy * dy) > 4) {
          didResize.current = true;
          onResizeStart(me);
        }
      };

      const onWindowUp = () => {
        window.removeEventListener('pointermove', onWindowMove);
        window.removeEventListener('pointerup', onWindowUp);
        resizeStartPos.current = null;
        didResize.current = false;
      };

      window.addEventListener('pointermove', onWindowMove);
      window.addEventListener('pointerup', onWindowUp);
    },
    [resizable, onResizeStart],
  );

  const handleApply = useCallback(
    (changes: PiPParamChanges) => {
      onApplyChanges?.(camera, changes);
    },
    [camera, onApplyChanges],
  );

  return (
    <div
      className="absolute overflow-hidden shadow-lg"
      style={{
        // Pixel mode (|value| > 1): use px units directly.
        // Fraction mode (|value| ≤ 1): use % of the container.
        // Negative = anchored from the opposite edge (right/bottom).
        left:
          camera.xPx !== undefined
            ? camera.xPx >= 0
              ? `${camera.xPx}px`
              : undefined
            : camera.x >= 0
              ? `${camera.x * 100}%`
              : undefined,
        right:
          camera.xPx !== undefined
            ? camera.xPx < 0
              ? `${-camera.xPx}px`
              : undefined
            : camera.x < 0
              ? `${-camera.x * 100}%`
              : undefined,
        top:
          camera.yPx !== undefined
            ? camera.yPx >= 0
              ? `${camera.yPx}px`
              : undefined
            : camera.y >= 0
              ? `${camera.y * 100}%`
              : undefined,
        bottom:
          camera.yPx !== undefined
            ? camera.yPx < 0
              ? `${-camera.yPx}px`
              : undefined
            : camera.y < 0
              ? `${-camera.y * 100}%`
              : undefined,
        width:
          camera.widthPx !== undefined
            ? `${camera.widthPx}px`
            : `${camera.width * 100}%`,
        height:
          camera.heightPx !== undefined
            ? `${camera.heightPx}px`
            : `${camera.height * 100}%`,
        borderRadius: '8px',
        border:
          hovered && clickable && !isDragging
            ? '1px solid rgba(255,255,255,0.6)'
            : isDragging
              ? '1px solid rgba(255,255,255,0.5)'
              : '1px solid rgba(255,255,255,0.2)',
        cursor: clickable && !isDragging ? 'pointer' : 'default',
        pointerEvents: 'auto',
        opacity: isDragging || isResizing ? 0.65 : 1,
        transition: 'border-color 0.15s ease, opacity 0.1s ease',
      }}
      onClick={
        clickable && !isDragging ? () => onCameraClick!(camera) : undefined
      }
      onMouseEnter={clickable ? () => setHovered(true) : undefined}
      onMouseLeave={clickable ? () => setHovered(false) : undefined}
    >
      <Canvas frameloop="demand" linear dpr={[1, 2]} gl={{ antialias: true }}>
        <PiPCameraSetup camera={camera} />
        <SceneBackground color={backgroundColor} />
        <SceneLighting
          sceneSettings={sceneSettings}
          showMovingLight={false}
          showGrid={false}
          wireframeOpacity={0}
          lights={lights}
          skipEnvironment={true}
        />
        <PiPModelRenderer
          geometry={geometry}
          coloredGeometries={coloredGeometries}
          color={color}
          materialSettings={sceneSettings.material}
        />
        <PostProcessing
          sceneSettings={{
            ...sceneSettings,
            ssao: { ...sceneSettings.ssao, enabled: false },
          }}
          multisampling={4}
        />
      </Canvas>

      {/* Optional label */}
      {camera.label && (
        <div className="absolute bottom-0 left-0 right-0 truncate bg-black/50 px-2 py-0.5 text-xs text-white">
          {camera.label}
        </div>
      )}

      {/* ── Context menu trigger (top-left) ── */}
      {hasMenu && <PiPContextMenu camera={camera} onApply={handleApply} />}

      {/* ── Move handle (top-right) ── */}
      {draggable && (
        <div
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            background: handleHovered
              ? 'rgba(255,255,255,0.25)'
              : 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            color: handleHovered
              ? 'rgba(255,255,255,0.9)'
              : 'rgba(255,255,255,0.5)',
            transition: 'background 0.15s ease, color 0.15s ease',
            zIndex: 10,
            touchAction: 'none',
          }}
          onMouseEnter={() => setHandleHovered(true)}
          onMouseLeave={() => setHandleHovered(false)}
          onPointerDown={handleGripPointerDown}
          onClick={(e) => e.stopPropagation()}
          title="Drag to move"
        >
          <DragGripIcon />
        </div>
      )}

      {/* ── Resize handle (bottom-right) ── */}
      {resizable && (
        <div
          style={{
            position: 'absolute',
            bottom: '4px',
            right: '4px',
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            background: resizeHandleHovered
              ? 'rgba(255,255,255,0.25)'
              : 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'nwse-resize',
            color: resizeHandleHovered
              ? 'rgba(255,255,255,0.9)'
              : 'rgba(255,255,255,0.5)',
            transition: 'background 0.15s ease, color 0.15s ease',
            zIndex: 10,
            touchAction: 'none',
          }}
          onMouseEnter={() => setResizeHandleHovered(true)}
          onMouseLeave={() => setResizeHandleHovered(false)}
          onPointerDown={handleResizePointerDown}
          onClick={(e) => e.stopPropagation()}
          title="Drag to resize"
        >
          <ResizeGripIcon />
        </div>
      )}
    </div>
  );
}
